import type { AuthApi, CognitoAuthApi, SessionExpiredInfo } from "./auth";
import { clearSyncedTokens } from "./syncedTokens";
import { broadcastAuthToOtherScreens } from "./desktopAuthSync";
import {
  clearEmergencyMarker,
  emergencyUser,
  setEmergencyMarker,
  type EmergencyRole,
} from "../exported/shared/utils/emergencySession";

const HOUSEKEEPING_TIMEOUT_MS = 3000;

export function createEmergencyAuthApi(role: EmergencyRole): AuthApi {
  const expiredSubs = new Set<(info: SessionExpiredInfo) => void>();

  function emitExpired(info: SessionExpiredInfo) {
    for (const s of expiredSubs) {
      try {
        s(info);
      } catch {
        /* swallow */
      }
    }
  }

  const unavailable = () =>
    Promise.reject(new Error("Not available in an emergency session"));

  return {
    emergencyRole: role,
    async getToken() {
      throw new Error("No session");
    },
    signIn: unavailable,
    confirmSignIn: unavailable,
    isTotpEnrolmentRequired: async () => false,
    startTotpSetup: unavailable,
    completeTotpSetup: unavailable,
    abandonSignIn: async () => {},
    resetPassword: unavailable,
    confirmResetPassword: unavailable,
    async signOut({ deliberate = true } = {}) {
      clearEmergencyMarker();
      clearSyncedTokens();
      emitExpired({ deliberate });
      void broadcastAuthToOtherScreens();
    },
    async loadCurrentUser() {
      return emergencyUser(role);
    },
    async getAuthTimeMs() {
      return null;
    },
    onTokenRefresh() {
      return () => {};
    },
    onSessionExpired(cb) {
      expiredSubs.add(cb);
      return () => {
        expiredSubs.delete(cb);
      };
    },
    onOfflineExpiry() {
      return () => {};
    },
    endOfflineSession() {},
    startTokenWatcher() {
      return () => {};
    },
    stopTokenWatcher() {},
  };
}

export async function startEmergencySession(
  role: EmergencyRole,
  cognito: Pick<CognitoAuthApi, "dropLingeringSession">,
): Promise<void> {
  await Promise.race([
    cognito.dropLingeringSession(),
    new Promise((resolve) => setTimeout(resolve, HOUSEKEEPING_TIMEOUT_MS)),
  ]);
  setEmergencyMarker(role);
}
